import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchConversionCampaignCommissionsThunk, fetchConversionCampaignsThunk, setConversionCampaignCommissionsFilters } from '../../redux-stores/slices/conversionCampaignSlice';
import { AppDispatch, RootState } from '../../redux-stores/store';
import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry from 'react-masonry-css';
import moment from 'moment';
import { formatAmount, formatDateRelative, generateUniqueKey, getStatusColorClass, textToTitleCase } from '../../utils/helpers';
import { ConversionCampaignCommission, ConversionCampaignCommissionStatusLabel } from '../../types/conversions';
import GroupedEarnings from './GroupedEarnings';
import "./ConversionCampaignCommissions.scss";
import Skeleton from 'react-loading-skeleton';
import FilterIcon from '../icons/FilterIcon';
import FiltersNewIcon from '../../assets/icons/FiltersNewIcon';
import ConversionCampaignCommissionPeriodFilters from './ConversionCampaignCommissionPeriodFilters';
const breakpointColumnsObj = {
    default: 2,
    600: 1,
};
const ConversionCampaignCommissions = () => {

    const dispatch = useDispatch<AppDispatch>();

    const navigate = useNavigate();
    const [showPeriodFiltersModal, setShowPeriodFiltersModal] = useState(false);

    const filters = useSelector((state: RootState) => state.conversionCampaigns.commissions.applied_filters);

    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    useEffect(() => {
        setFilterId(generateUniqueKey(filters, ['page']));
    }, [filters])
    const commissionsData = useSelector(
        (state: RootState) => state.conversionCampaigns.commissions?.filtered_lists?.[filterId]
    );

    const commissions = commissionsData?.list ?? [];
    const page = commissionsData?.page ?? 1;
    const has_more = commissionsData?.has_more ?? true;

    const commissionsLoading = useSelector((state: RootState) => state.conversionCampaigns.commissionsLoading);

    const nextPage = () => {
        if (has_more) {
            const nextPageNumber = page + 1;
            fetchConversionCampaignsCommissions(nextPageNumber)
        }

    }

    const setFilters = (filters: { start_date?: string, end_date?: string, month?: string }) => {
        dispatch(setConversionCampaignCommissionsFilters(filters));
    }

    const fetchConversionCampaignsCommissions = useCallback(async (pageNumber: number) => {
        await dispatch(fetchConversionCampaignCommissionsThunk({ page: pageNumber, filters }));
    }, [dispatch, filters]);

    useEffect(() => {
        fetchConversionCampaignsCommissions(1);
    }, [filters])


    const groupedTxns = useMemo(() => {
        let grouped: { date: string, commissions: ConversionCampaignCommission[] }[] = [];
        let dates: string[] = [];
        for (let commission of commissions) {
            let relativeDate = formatDateRelative(commission.created_at);
            if (!dates.includes(relativeDate)) {
                dates.push(relativeDate);
                grouped.push({ date: relativeDate, commissions: [commission] });
            } else {
                grouped[dates.indexOf(relativeDate)].commissions.push(commission);
            }
        }
        return grouped;
    }, [commissions]);

    const last5Months = useMemo(() => {
        // Return last 5 months in format 'MMMM'
        return Array.from({ length: 5 }, (_, i) => ({ label: moment().subtract(i, 'months').format('MMMM'), start_date: moment().subtract(i, 'months').startOf('month').format('YYYY-MM-DD'), end_date: moment().subtract(i, 'months').endOf('month').format('YYYY-MM-DD'), month: moment().subtract(i, 'months').format('YYYY-MM') }))
    }, []);

    const applyMonthFilter = (month: any) => {
        if (filters.month === month.month) {
            dispatch(setConversionCampaignCommissionsFilters({}));
        } else {
            dispatch(setConversionCampaignCommissionsFilters({ ...filters, month: month.month, start_date: month.start_date, end_date: month.end_date }));
        }
    }



    return (
        <>
            <div className=''>
                <GroupedEarnings />
                <div className=''>
                    <h4 className='px-3 fw-500 fs-24 text-dark'>Transactions</h4>
                    <div className="conversion-campaign-commissions__toolbar px-3 mt-2">
                        <span onClick={() => setShowPeriodFiltersModal(true)} className="conversion-campaign-commissions__month-chip text-primary">
                            <FiltersNewIcon />
                            Filters
                        </span>
                        <div className="conversion-campaign-commissions__months">
                            {last5Months.map((month, i) => {
                                return (
                                    <span onClick={() => applyMonthFilter(month)} key={i} className={`conversion-campaign-commissions__month-chip ${filters.month === month.month ? 'is-active' : ''}`}>{month.label}</span>
                                )
                            })}
                        </div>
                    </div>
                    <InfiniteScroll
                        dataLength={commissions.length} // Number of currently loaded campaigns
                        next={nextPage}
                        hasMore={has_more} // True if there are more campaigns to load
                        loader={<></>}
                        className='d-flex flex-column gap-3 p-3'
                    >
                        {
                            groupedTxns.map((group, i) => {
                                return (
                                    <div className="d-flex flex-column gap-2 position-relative" key={group.date}>
                                        <div className="conversion-campaign-commissions__date-sticky">
                                            <h4 className='fw-500 fs-16 text-dark'>{group.date}</h4>
                                        </div>
                                        <div className="app-card p-0">
                                            {
                                                group.commissions.map((commission, i) => {
                                                    return (
                                                        <div className={`grouped-txn-item d-flex flex-column gap-2 ${i === group.commissions.length - 1 ? 'border-bottom-0' : ''}`} key={i}>
                                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                                <div className="grouped-txn-item-brand d-flex align-items-center gap-075">
                                                                    <div className="grouped-txn-item-brand-logo bg-image bgc-white" style={{ backgroundImage: `url(${commission.campaign?.img_url})` }}></div>
                                                                    <span className='fs-16 fw-600 text-dark'>{commission.campaign.name}</span>
                                                                </div>
                                                                <div className="d-flex flex-column gap-1">
                                                                    <span className='fs-12 fw-400 text-muted'>Your Commission</span>
                                                                    <span className={commission.status === 'paid' ? 'text-success fw-700 fs-16' : 'text-dark fw-500 fs-16'}>{formatAmount(commission.commission_amount)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                                <div className="d-flex flex-column gap-1">
                                                                    <span className='fs-12 fw-400 text-muted'>Campaign Type: <span className="text-dark">{commission.campaign.payout_model}</span></span>
                                                                    <span className='fs-12 fw-400 text-muted'>Sale Amount: <span className="text-dark">{formatAmount(commission.sale_amount)}</span></span>
                                                                </div>
                                                                <span className={`grouped-txn-item-status ${commission.status.toLowerCase()}`}>{textToTitleCase(commission.status)}</span>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                )
                            })
                        }
                        {
                            (commissionsLoading) &&
                            <Skeleton height={120} width={'100%'} borderRadius={'1rem'} count={5} className='mb-3' />
                        }
                        {
                            !commissionsLoading && commissions.length === 0 &&
                            <div className="d-flex w-100 justify-content-center align-items-center my-1">
                                <div className="length-zero my-1">
                                    <h3>
                                        No earnings yet!
                                    </h3>
                                </div>
                            </div>
                        }
                    </InfiniteScroll>
                </div>

            </div>
            <ConversionCampaignCommissionPeriodFilters isActive={showPeriodFiltersModal} onClose={() => setShowPeriodFiltersModal(false)} />
        </>

    )
}

export default ConversionCampaignCommissions